import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { AppState } from '../../models/state/appState';

import moment from 'moment';
import { getUserSetting } from '../../services/helpers';

import { getLocale } from '../../constants';
import { guessTimeZone, inTimeZone } from '../../services/timeZone';

interface Props {
  /**
   * The date/time value to display.
   */
  value: Date;

  /**
   * The format to use when displaying the full date/time.
   */
  fullTimeFormat?: string;

  /**
   * The number of milliseconds between updates.
   */
  updateDelay?: number;

  /**
   * The value to use when calculating the point at which the date and time
   * will display in full as opposed to a "from now" style string.
   */
  displaySwitchDelayValue?: number;

  /**
   * The unit to use when calculating the point at which the date and time
   * will display in full as opposed to a "from now" style string.
   */
  displaySwitchDelayUnit?: moment.unitOfTime.DurationConstructor;
}

export default function LiveFormattedDateTime({
  value,
  updateDelay = 10000,
  displaySwitchDelayValue = 1,
  displaySwitchDelayUnit = 'hour',
}: Props) {
  const timeZone = useSelector<AppState, string>(
    (state) => getUserSetting('timezone', state.userSettings.userSettings)?.settingValue || guessTimeZone()
  );
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), updateDelay);
    return () => clearInterval(interval);
  }, [updateDelay]);

  const momentValue = inTimeZone(moment.utc(value), timeZone);
  momentValue.locale(getLocale(timeZone));
  const fullTimePoint = moment.utc(value).add(displaySwitchDelayValue as any, displaySwitchDelayUnit);
  const displayValue = moment.utc() > fullTimePoint ? momentValue.format('DD/MM/YYYY LT') : momentValue.fromNow();

  return <span>{displayValue}</span>;
}
